import { memo, useCallback, useMemo } from 'react';
import { List } from '@divlab/divanui';

import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import * as RelatedProductsQueries from '@Queries/RelatedProducts';
import { useDeps } from '@Contexts/DI';
import useTranslation from '@Queries/useTranslation';
import RelatedProductCard from './elements/RelatedProductCard';
import styles from './RelatedProductsList.module.css';

import type { WarrantyData } from '@Pages/PageProduct/typings';
import type { FC, HTMLAttributes } from 'react';
import type { IRelatedProductsFromPage } from '@Types/RelatedProducts';
import type { ProductData } from '@Types/Product';

export interface RelatedProductsProps extends HTMLAttributes<HTMLDivElement> {
  mainId: number;
  forTitles: IRelatedProductsFromPage;
  warranty?: WarrantyData;
}

const defaultTitle = 'Сопутствующие товары карточка товара';
const kitTitle = 'Вместе дешевле карточка товара';

const RelatedProductsList: FC<RelatedProductsProps> = (props) => {
  const { mainId, forTitles, warranty, ...restProps } = props;
  const { t } = useTranslation();
  const { analytics } = useDeps();
  const page = PageProductStore.useData();
  const isKits = page.kits?.length > 0;
  const analyticsTitle = isKits ? kitTitle : defaultTitle;
  const selectedProducts = RelatedProductsQueries.useSortedRelatedProductsBy('quantity', mainId);
  const { mutateAsync: changeQuantity } = RelatedProductsQueries.useChangeQuantity(mainId);
  const { apply } = RelatedProductsQueries.useApply(mainId);

  const groupLabel = useMemo(() => {
    return forTitles.map((category, index) => {
      const prefix = index === 0 ? '' : ', ';

      return (
        <span key={index}>
          {prefix}
          {category.add}
          {category.promoText && <span className={styles.promoText}>{category.promoText}</span>}
        </span>
      );
    });
  }, [forTitles]);

  const handleChangeQuantity = useCallback(
    async (product: ProductData, quantity: number, position: number) => {
      if (!changeQuantity) return;

      const currentQuantity = product.quantity || 0;

      if (currentQuantity !== quantity) {
        const eventType = currentQuantity > quantity ? 'remove' : 'add';
        analytics.dispatchEvent(`relatedProduct.${eventType}`, {
          name: product.name,
          id: product.id,
          position,
        });
      }

      await changeQuantity({ product, quantity });
      apply();
    },
    [analytics, apply, changeQuantity],
  );

  const handleClick = useCallback(() => {
    ModalWindows.open('RelatedProducts', { mainId, warranty, analyticsTitle });
  }, [warranty, mainId, analyticsTitle]);

  return (
    <div {...restProps}>
      <div className={styles.groupHead} onClick={handleClick} data-testid='add'>
        <div className={styles.iconPlus} />
        <div className={styles.label}>{t('add-relative-products')}</div>

        <span className={styles.groupLabel}>{groupLabel}</span>
      </div>

      {selectedProducts?.length > 0 && (
        <List
          className={styles.list}
          items={selectedProducts}
          renderChild={(product: ProductData, index) => (
            <RelatedProductCard
              className={styles.product}
              product={product}
              position={index}
              onChangeQuantity={handleChangeQuantity}
              data-testid={`add_item${index + 1}`}
            />
          )}
        />
      )}
    </div>
  );
};

export default memo(RelatedProductsList);
